import { useQuery } from "@tanstack/react-query";
import { api, type SellerShop, type Storefront } from "@/api";
import { useSellers } from "./use-sellers";

export const storefrontKeys = {
	all: ["storefronts"] as const,
	products: (shopId: string) => ["storefronts", "products", shopId] as const,
	analytics: (shopId: string) => ["storefronts", "analytics", shopId] as const,
	salesSummary: ["storefronts", "sales-summary"] as const,
};

export function useStorefronts(enabled = true) {
	return useQuery({
		queryKey: storefrontKeys.all,
		queryFn: () => api.storefronts.list(),
		enabled,
	});
}

/**
 * Куда вести из «Финансов» и «Заказов» к магазину, известному по id. Slug из
 * снимка заказа не годится: он может разойтись с витриной (демо-заказы
 * НатурТекстиль хранят «naturtextil», у витрины «natur-tekstil» → 404).
 * Поэтому - как в списке «Магазины» (lib/shop-rows.ts): витрина из списка по
 * id → её slug; витрины в списке нет (заблокирована, не опубликована) →
 * карточка продавца; продавца тоже нет → сам id (шлюз ищет магазин и по нему).
 * `listed` - витрина есть в публичном списке, её карточку можно запрашивать.
 */
export type ShopRoute = { key: string; href: string; listed: boolean };

export const shopRoute = (
	storefronts: readonly Storefront[] | undefined,
	sellers: readonly SellerShop[] | undefined,
	shopId: string,
): ShopRoute => {
	const storefront = storefronts?.find((s) => s.id === shopId);
	if (storefront) return { key: storefront.slug, href: `/shops/${storefront.slug}`, listed: true };
	const seller = sellers?.find((s) => (s.shopId ?? s.id) === shopId);
	return {
		key: shopId,
		href: seller ? `/shops/seller/${seller.id}` : `/shops/${shopId}`,
		listed: false,
	};
};

/**
 * `shopRoute` с загрузкой списков. `ready` - списки получены (или не нужны);
 * `fetchStorefront` - запрашивать ли карточку витрины: да, если она в списке
 * или список не загрузился (тогда пробуем по id).
 */
export function useShopRoute(shopId: string | undefined) {
	const storefronts = useStorefronts(!!shopId);
	const sellers = useSellers(!!shopId);
	if (!shopId) return { key: "", href: "", listed: false, ready: true, fetchStorefront: true };
	const route = shopRoute(storefronts.data, sellers.data, shopId);
	return {
		...route,
		ready: !storefronts.isPending && !sellers.isPending,
		fetchStorefront: route.listed || storefronts.isError,
	};
}

export function useStorefrontProducts(shopId: string) {
	return useQuery({
		queryKey: storefrontKeys.products(shopId),
		queryFn: () => api.storefronts.products(shopId),
		enabled: !!shopId,
	});
}

export function useShopAnalytics(shopId: string, enabled = true) {
	return useQuery({
		queryKey: storefrontKeys.analytics(shopId),
		queryFn: () => api.storefronts.analytics(shopId),
		enabled: enabled && !!shopId,
	});
}

export function useShopSalesSummary() {
	return useQuery({
		queryKey: storefrontKeys.salesSummary,
		queryFn: () => api.storefronts.salesSummary(),
	});
}
